"use client";

/**
 * MarkdownViewerSidebar — desktop right-sidebar panel that slides in beside
 * the chat area to display a Markdown document.
 *
 * Uses ``motion`` (Framer Motion successor, already a dependency) for the
 * slide-in animation. The sidebar is a flex sibling of the chat area, so
 * the chat area shrinks when the sidebar is open — the user can read the
 * document while continuing to see the conversation.
 *
 * Variable width controlled by a draggable resize handle on the left
 * edge. The width is managed by the parent via the ``useResizableWidth``
 * hook and passed down as a prop. Includes a header bar (filename + close
 * button) and a scrollable content area.
 */

import { useRef, useState } from "react";
import { motion } from "motion/react";
import { X, FileText } from "lucide-react";
import type { Editor } from "@tiptap/core";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { MarkdownViewerToolbar } from "@/components/markdown-viewer-toolbar";
import { MarkdownViewerPinButton } from "@/components/markdown-viewer-pin-button";
import { MarkdownViewerSaveStatus } from "@/components/markdown-viewer-save-status";
import { MarkdownOutline, MarkdownOutlineTop } from "@/components/markdown-outline";
import { useTranslation } from "react-i18next";
import type { AccessTokenGetter } from "@/lib/utils";

export interface MarkdownViewerSidebarProps {
  content: string | null;
  renderContent?: string | null;
  loading: boolean;
  error: string | null;
  fileName: string;
  /** Retrieves a WorkOS token for authenticated PDF downloads. */
  getToken: AccessTokenGetter;
  onClose: () => void;
  onNavigateFile?: (url: string, fileName: string) => void;
  onRetry?: () => void;
  /**
   * ``true`` briefly after a background HEAD-poll refresh delivers new
   * content. Shows a subtle "updated" dot indicator next to the filename.
   */
  refreshed?: boolean;
  /** Whether this document is in Tiptap edit mode. */
  editable?: boolean;
  /** Whether editor Markdown differs from its last successful save. */
  dirty?: boolean;
  /** True while the parent is saving via the signed PUT endpoint. */
  saving?: boolean;
  /** A save-specific error that leaves the current document visible. */
  saveError?: string | null;
  /** A detect-only poll noticed a newer remote version. */
  remoteChange?: boolean;
  /** A stale `If-Match` response blocks another normal Save. */
  conflict?: boolean;
  /** A transport/5xx save needs explicit reconciliation before retrying. */
  saveOutcomeUnknown?: boolean;
  compatibilityNotice?: boolean;
  compatibilityNoticeKey?: string;
  onReloadAndDiscard?: () => void | Promise<void>;
  onCheckSaveOutcome?: () => void | Promise<void>;
  /** Latest Markdown, used to keep copy/download serialisation current. */
  currentMarkdown?: string | null;
  onToggleEdit?: () => void;
  onSave?: (content?: string) => boolean | void | Promise<boolean | void>;
  onMarkdownLoaded?: (markdown: string) => void;
  onMarkdownChange?: (markdown: string) => void;
  /** Whether the sidebar should animate when it mounts or unmounts. */
  animate?: boolean;
  /** Sidebar width in pixels (controlled by parent via useResizableWidth). */
  width: number;
  /** Signed file URL for the "open in new tab" pop-out. */
  fileUrl?: string;
  /** Called when the user wants to open the viewer in a new tab. */
  onOpenInNewWindow?: () => void;
  /** Canonical path decoded from the currently viewed normal file link. */
  filePath?: string | null;
  onPinFile?: (path: string) => Promise<void> | void;
  pinned?: boolean;
  pinning?: boolean;
  /** Drag-handle pointer handlers from useResizableWidth. */
  onResizePointerDown: (e: React.PointerEvent) => void;
  onResizePointerMove: (e: React.PointerEvent) => void;
  onResizePointerUp: (e: React.PointerEvent) => void;
  onResizeDoubleClick: () => void;
}

export function MarkdownViewerSidebar({
  content,
  renderContent,
  loading,
  error,
  fileName,
  getToken,
  onClose,
  onNavigateFile,
  onRetry,
  refreshed,
  editable = false,
  dirty = false,
  saving = false,
  saveError = null,
  remoteChange = false,
  conflict = false,
  saveOutcomeUnknown = false,
  compatibilityNotice = false,
  compatibilityNoticeKey = "viewer.collabReadOnly",
  onReloadAndDiscard,
  onCheckSaveOutcome,
  currentMarkdown,
  onToggleEdit,
  onSave,
  onMarkdownLoaded,
  onMarkdownChange,
  animate = true,
  width,
  fileUrl,
  onOpenInNewWindow,
  filePath = null,
  onPinFile,
  pinned = false,
  pinning = false,
  onResizePointerDown,
  onResizePointerMove,
  onResizePointerUp,
  onResizeDoubleClick,
}: MarkdownViewerSidebarProps) {
  const { t } = useTranslation();
  const [editor, setEditor] = useState<Editor | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  return (
    <motion.div
      initial={animate ? { x: "100%" } : false}
      animate={{ x: 0 }}
      exit={animate ? { x: "100%" } : undefined}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="relative flex h-full flex-col border-l bg-background shrink-0 min-w-0 overflow-hidden"
      style={{ width }}
      data-testid="markdown-viewer-sidebar"
    >
      {/* Resize handle — sits on the left edge, full height */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("viewer.dragToResize")}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onDoubleClick={onResizeDoubleClick}
        className="absolute -left-[3px] top-0 z-20 h-full w-1.5 cursor-col-resize touch-none select-none hover:bg-primary/30 active:bg-primary/40 transition-colors"
        data-testid="markdown-viewer-resize-handle"
      />
      {/* Header */}
      <div className="flex items-center justify-between border-b px-3 py-2.5 min-h-[53px] gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <MarkdownViewerSaveStatus
            contentLoaded={currentMarkdown != null}
            editable={editable}
            hasSaveHandler={Boolean(onSave) && !conflict && !saveOutcomeUnknown}
            dirty={dirty}
            saving={saving}
            saveError={saveError}
          />
          <span
            className="truncate text-sm font-semibold"
            title={fileName}
            data-testid="markdown-viewer-filename"
          >
            {fileName}
          </span>
          {refreshed && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-blue-500"
              title={t("viewer.contentUpdated")}
              data-testid="markdown-viewer-refreshed-indicator"
            />
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <MarkdownViewerPinButton
            filePath={filePath}
            onPinFile={onPinFile}
            pinned={pinned}
            pinning={pinning}
          />
          <MarkdownViewerToolbar
            editor={editor}
            sourceMarkdown={dirty ? currentMarkdown : content}
            fileName={fileName}
            getToken={getToken}
            fileUrl={fileUrl}
            onOpenInNewWindow={onOpenInNewWindow}
            editable={editable}
            dirty={dirty}
            saving={saving}
            saveError={saveError}
            saveBlocked={conflict || saveOutcomeUnknown}
            onToggleEdit={onToggleEdit}
            onSave={conflict || saveOutcomeUnknown ? undefined : onSave}
            contentVersion={currentMarkdown}
          />
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={onClose}
            aria-label={t("viewer.closeViewer")}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>

      {/* The outline is an overlay on this content region, never a sidebar column. */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {/* Content */}
        <div ref={contentScrollRef} className="h-full min-w-0 overflow-y-auto overflow-x-hidden p-4">
          {compatibilityNotice && <p role="status" className="mb-2 rounded bg-muted p-2 text-xs">{t(compatibilityNoticeKey)}</p>}
          {loading && content == null ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Spinner className="size-5" />
              <p className="text-sm">{t("common:status.loadingDocument")}</p>
            </div>
          ) : error && content == null ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center p-4">
              <p className="text-sm text-destructive">{error}</p>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  {t("common:actions.retry")}
                </Button>
              )}
            </div>
          ) : content != null ? (
            <>
              {remoteChange && !conflict && !saveOutcomeUnknown && (
                <p className="mb-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  {t("viewer.remoteChange")}
                </p>
              )}
              {saveOutcomeUnknown && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("viewer.saveOutcomeUnknown")}</span>
                  {onCheckSaveOutcome && (
                    <Button size="sm" variant="outline" onClick={() => void onCheckSaveOutcome()}>{t("viewer.checkLatest")}</Button>
                  )}
                </div>
              )}
              {conflict && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("viewer.fileChangedElsewhere")}</span>
                  {onReloadAndDiscard && (
                    <Button size="sm" variant="outline" onClick={() => void onReloadAndDiscard()}>{t("viewer.reloadAndDiscard")}</Button>
                  )}
                </div>
              )}
              <MarkdownOutlineTop
                editor={editor}
                scrollContainerRef={contentScrollRef}
                contentVersion={currentMarkdown ?? content}
              />
              <MarkdownViewer
                content={content}
                renderContent={renderContent}
                fileName={fileName}
                editable={editable}
                saving={saving}
                onNavigateFile={onNavigateFile}
                onEditorReady={setEditor}
                onMarkdownLoaded={onMarkdownLoaded}
                onMarkdownChange={onMarkdownChange}
                preserveScrollOnContentChange={refreshed && !editable}
                scrollContainerRef={contentScrollRef}
              />
            </>
          ) : null}
        </div>
        <MarkdownOutline
          editor={editor}
          scrollContainerRef={contentScrollRef}
          contentVersion={currentMarkdown ?? content}
        />
      </div>
    </motion.div>
  );
}
